import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService, type AuthUser as APIAuthUser } from '../services/authService'

export interface AuthUser {
  id: number
  name: string
  email: string
  role: string
  permissions: string[]
}

export interface AuthState {
  // Authentication status
  isAuthenticated: boolean
  
  // Current step in auth flow
  currentStep: 'userInfo' | 'otpVerification' | 'consentAcceptance'
  
  // User data
  user: AuthUser | null
  
  // Form states
  isLoading: boolean
  isSubmitting: boolean
  error: string | null
  
  // OTP state
  sessionId: string | null
  isOtpVerified: boolean
  currentEmail: string | null // Store email during OTP flow
  
  // Consent acceptance
  consentAccepted: boolean
  
  // Logout state
  isLoggingOut: boolean
  
  // Actions
  setCurrentStep: (step: AuthState['currentStep']) => void
  setUser: (user: AuthUser) => void
  setLoading: (loading: boolean) => void
  setSubmitting: (submitting: boolean) => void
  setError: (error: string | null) => void
  setSessionId: (sessionId: string | null) => void
  setOtpVerified: (verified: boolean) => void
  setConsentAccepted: (accepted: boolean) => void
  submitUserInfo: (email: string, fullName: string) => Promise<void>
  verifyOtp: (otp: string) => Promise<boolean>
  resendOtp: () => Promise<void>
  submitConsent: (consentData: {
    termsAccepted: boolean
    privacyPolicyAccepted: boolean
    marketingOptIn?: boolean
    dataProcessingConsent: boolean
  }) => Promise<void>
  completeAuth: () => void
  logout: () => Promise<void>
  resetAuth: () => void
  checkAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      currentStep: 'userInfo',
      user: null,
      isLoading: false,
      isSubmitting: false,
      error: null,
      sessionId: null,
      isOtpVerified: false,
      currentEmail: null,
      consentAccepted: false,
      isLoggingOut: false,

      // Actions
      setCurrentStep: (step) => set({ currentStep: step }),
      
      setUser: (user) => set({ user }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      setSubmitting: (submitting) => set({ isSubmitting: submitting }),
      
      setError: (error) => set({ error }),
      
      setSessionId: (sessionId) => set({ sessionId }),
      
      setOtpVerified: (verified) => set({ isOtpVerified: verified }),
      
      setConsentAccepted: (accepted) => set({ consentAccepted: accepted }),

      submitUserInfo: async (email: string, fullName: string) => {
        try {
          set({ isSubmitting: true, error: null })
          
          // Validate inputs
          if (!fullName.trim()) {
            throw new Error('Full name is required')
          }
          
          if (!email.trim() || !email.includes('@')) {
            throw new Error('Valid email is required')
          }
          
          // Request OTP from identity server
          const response = await authService.requestOTP(email, fullName)
          
          if (response.success && response.sessionId) {
            set({ 
              sessionId: response.sessionId,
              currentEmail: email,
              currentStep: 'otpVerification' 
            })
          } else {
            throw new Error('Failed to send OTP. Please try again.')
          }
          
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to submit user information' })
        } finally {
          set({ isSubmitting: false })
        }
      },

      verifyOtp: async (inputOtp) => {
        try {
          set({ isSubmitting: true, error: null })
          
          const { sessionId } = get()
          if (!sessionId) {
            throw new Error('No session ID found. Please restart the authentication process.')
          }
          
          // Verify OTP with identity server
          const response = await authService.verifyOTP(sessionId, inputOtp)
          
          if (response.success && response.user) {
            // Convert API user to our AuthUser format
            const user: AuthUser = {
              id: response.user.id,
              name: response.user.fullName,
              email: response.user.email,
              role: response.user.role,
              permissions: response.user.permissions
            }
            
            set({ 
              user,
              isOtpVerified: true, 
              currentStep: 'consentAcceptance' 
            })
            return true
          } else {
            throw new Error('Invalid OTP code. Please check and try again.')
          }
          
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'OTP verification failed' })
          return false
        } finally {
          set({ isSubmitting: false })
        }
      },

      resendOtp: async () => {
        try {
          set({ isSubmitting: true, error: null })
          
          const { currentEmail, user } = get()
          if (!currentEmail && !user?.email) {
            throw new Error('No email found. Please restart the authentication process.')
          }
          
          const email = currentEmail || user?.email || ''
          const fullName = user?.name || 'User'
          
          // Request new OTP
          const response = await authService.resendOTP(email, fullName)
          
          if (response.success && response.sessionId) {
            set({ 
              sessionId: response.sessionId,
              currentEmail: email
            })
          } else {
            throw new Error('Failed to resend OTP. Please try again.')
          }
          
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to resend OTP' })
          throw error
        } finally {
          set({ isSubmitting: false })
        }
      },

      submitConsent: async (consentData) => {
        try {
          set({ isSubmitting: true, error: null })
          
          // Submit consent to identity server
          const response = await authService.submitConsent(consentData)
          
          if (response.success) {
            set({ 
              consentAccepted: true,
              isAuthenticated: true,
              currentStep: 'userInfo' // Reset for next time
            })
          } else {
            throw new Error('Failed to process consent. Please try again.')
          }
          
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Consent submission failed' })
          throw error
        } finally {
          set({ isSubmitting: false })
        }
      },

      completeAuth: () => {
        set({ 
          isAuthenticated: true,
          currentStep: 'userInfo' // Reset for next time
        })
      },

      logout: async () => {
        try {
          set({ isLoggingOut: true })
          
          // Simulate logout process for better UX (minimum time to show the modal)
          await new Promise(resolve => setTimeout(resolve, 1500))
          
          // Clear tokens from auth service
          authService.clearTokens()
          
          // Clear all auth state
          set({
            isAuthenticated: false,
            currentStep: 'userInfo',
            user: null,
            isLoading: false,
            isSubmitting: false,
            error: null,
            sessionId: null,
            isOtpVerified: false,
            currentEmail: null,
            consentAccepted: false,
            isLoggingOut: false
          })
          
        } catch (error) {
          set({ isLoggingOut: false })
          throw error
        }
      },

      resetAuth: () => {
        authService.clearTokens()
        set({
          isAuthenticated: false,
          currentStep: 'userInfo',
          user: null,
          isLoading: false,
          isSubmitting: false,
          error: null,
          sessionId: null,
          isOtpVerified: false,
          currentEmail: null,
          consentAccepted: false,
          isLoggingOut: false
        })
      },

      checkAuth: async () => {
        const tokens = authService.getStoredTokens()
        if (!tokens.accessToken) {
          set({ isAuthenticated: false, user: null })
          return
        }

        set({ isLoading: true })
        try {
          const response = await authService.validateToken()
          if (response.valid && response.user) {
            // Convert API user to our AuthUser format
            const user: AuthUser = {
              id: response.user.id,
              name: response.user.fullName,
              email: response.user.email,
              role: response.user.role,
              permissions: response.user.permissions
            }
            
            set({
              user,
              isAuthenticated: true,
              consentAccepted: authService.hasValidConsent(),
              isLoading: false
            })
          } else {
            get().logout()
          }
        } catch (error) {
          console.error('Auth check failed:', error)
          get().logout()
          set({ isLoading: false })
        }
      }
    }),
    {
      name: 'stratiqx-auth', // localStorage key
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        consentAccepted: state.consentAccepted
      })
    }
  )
)
